<!--
  EmployerSubnav — section-nav tab strip rendered below the shared
  JobsProfileNavbar for authenticated employer routes.

  Approval gating: Inbox requires an approved employer account (see
  router.ts meta.requiresApproval + EmployerLayout.enforceApprovalGuard).
  Surface that state visibly here instead of silently redirecting.

  Styled with Tailwind utilities + project tokens — no scoped CSS.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useEmployerSessionStore } from '../../stores/employerSession';

const route = useRoute();
const session = useEmployerSessionStore();

// Shared classes kept in one place so the active/inactive/disabled variants stay aligned.
const baseTab = 'inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap';
const activeTab = 'border-[color:var(--jb-primary)] text-[color:var(--jb-primary)]';
const inactiveTab = 'border-transparent text-ink-2 hover:text-ink';
const disabledTab = 'border-transparent text-ink-3 opacity-50 cursor-not-allowed pointer-events-none';

function tabClass(active: boolean, disabled = false) {
    if (disabled) return [baseTab, disabledTab];
    return [baseTab, active ? activeTab : inactiveTab];
}

const isDashboard = computed(() => route.name === 'employer-dashboard');
const isInbox = computed(() => route.name === 'employer-inbox');
const isCandidates = computed(() => route.name === 'employer-candidates');
const isProfile = computed(() => route.name === 'employer-profile');
const isTeam = computed(() => route.name === 'employer-team');
const isBilling = computed(() => route.name === 'employer-billing');

// Mirrors router meta.requiresApproval for employer-inbox. Keep in sync if
// more routes gain the same meta flag.
const inboxLocked = computed(() => !session.isApproved);
const inboxTitle = computed(() =>
    inboxLocked.value ? 'Inbox unlocks once your account is approved' : ''
);

// Approval is the only hard block (router meta.requiresApproval): an unapproved
// employer has nothing to buy yet. An APPROVED employer without a plan must still
// reach the page, because the locked panel behind this tab is where the subscribe
// CTA lives. Disabling it there left the paywall reachable only by typing the URL.
const candidatesLocked = computed(() => !session.isApproved);
const candidatesTitle = computed(() =>
    candidatesLocked.value ? 'Candidate search unlocks once your account is approved' : ''
);

// Approved but unpaid: reachable, badged with a lock so the gate is still legible.
const candidatesNeedsPlan = computed(() => session.isApproved && !session.hasActivePlan);
</script>

<template>
    <div class="bg-white border-b border-[color:var(--jb-border)]">
        <div class="max-w-[1128px] mx-auto px-6 flex gap-1 overflow-x-auto">
            <router-link :to="{ name: 'employer-dashboard' }" :class="tabClass(isDashboard)">
                <Icon name="chart-bar" :size="16" />
                <span>Home</span>
            </router-link>
            <router-link
                v-if="!inboxLocked"
                :to="{ name: 'employer-inbox' }"
                :class="tabClass(isInbox)"
            >
                <Icon name="inbox" :size="16" />
                <span>Inbox</span>
            </router-link>
            <span
                v-else
                :class="tabClass(false, true)"
                :title="inboxTitle"
                aria-disabled="true"
            >
                <Icon name="inbox" :size="16" />
                <span>Inbox</span>
            </span>
            <router-link
                v-if="!candidatesLocked"
                :to="{ name: 'employer-candidates' }"
                :class="tabClass(isCandidates)"
                :title="candidatesNeedsPlan ? 'Subscribe to search the verified candidate pool' : ''"
            >
                <Icon name="user-check" :size="16" />
                <span>Candidates</span>
                <Icon v-if="candidatesNeedsPlan" name="lock" :size="13" class="text-ink-3" />
            </router-link>
            <span
                v-else
                :class="tabClass(false, true)"
                :title="candidatesTitle"
                aria-disabled="true"
            >
                <Icon name="user-check" :size="16" />
                <span>Candidates</span>
            </span>
            <router-link :to="{ name: 'employer-profile' }" :class="tabClass(isProfile)">
                <Icon name="building" :size="16" />
                <span>Company</span>
            </router-link>
            <router-link :to="{ name: 'employer-team' }" :class="tabClass(isTeam)">
                <Icon name="users" :size="16" />
                <span>Team</span>
            </router-link>
            <!-- Never gated: an employer must be able to reach billing even when suspended,
                 otherwise they can be charged with no way to cancel. -->
            <router-link :to="{ name: 'employer-billing' }" :class="tabClass(isBilling)">
                <Icon name="credit-card" :size="16" />
                <span>Billing</span>
            </router-link>
        </div>
    </div>
</template>
